/**
 * Every API route this app calls, as one typed function each.
 *
 * The point of gathering them here rather than sprinkling `request()` calls
 * through components: this file is checkable against
 * `apps/api/src/routes/**` by reading the two side by side. Paths, methods and
 * response types all come from the contracts, so a route that changes shape
 * breaks the build here — at the seam — rather than at render time in a screen.
 *
 * Not one type is declared in this file. Every argument and every return is
 * inferred from `@svcdue/contracts`, because that is the wire format and a
 * second opinion about it would only ever be wrong.
 */

import type {
  AdminUserDetail,
  CarDto,
  CostsResponse,
  CreateCarRequest,
  CreateEventRequest,
  CreateGarageRequest,
  CreateImageUploadRequest,
  CreateImageUploadResponse,
  CreateItemRequest,
  CreateItemsRequest,
  CreateServiceLogRequest,
  CompleteEventRequest,
  CreateDriverRequest,
  DashboardResponse,
  DeleteUserRequest,
  DeleteUserResponse,
  DriverDto,
  EventDto,
  GarageDto,
  HistoryResponse,
  InviteMemberRequest,
  ItemDto,
  ListUsersQuery,
  ListUsersResponse,
  MarkCheckedRequest,
  MeResponse,
  MemberDto,
  PublicGarageResponse,
  ServiceLogDto,
  SetPublicResponse,
  StatsResponse,
  TimelineResponse,
  UpdateCarRequest,
  UpdateDriverRequest,
  UpdateEventRequest,
  UpdateGarageRequest,
  UpdateItemRequest,
  UpdateMeRequest,
} from '@svcdue/contracts';

import { request } from './client';

// --- identity -------------------------------------------------------------

export const getMe = () => request<MeResponse>('/me');

/**
 * The caller's own reminder preferences (§6). Like `deleteMe`, the server takes
 * the user from the token, so this cannot touch another account.
 */
export const updateMe = (body: UpdateMeRequest) => request<MeResponse>('/me', { method: 'PATCH', body });

/**
 * Delete the signed-in account and every garage it owns (§11). The server reads
 * the caller from the token — never a user id from here — so this can only ever
 * erase the user making the call. `confirmEmail` must match, or the API 400s.
 */
export const deleteMe = (body: DeleteUserRequest) =>
  request<DeleteUserResponse>('/me', { method: 'DELETE', body });

// --- garages --------------------------------------------------------------

export const listGarages = () => request<GarageDto[]>('/garages');

export const getGarage = (garageId: string) => request<GarageDto>(`/garages/${garageId}`);

export const createGarage = (body: CreateGarageRequest) =>
  request<GarageDto>('/garages', { method: 'POST', body });

export const updateGarage = (garageId: string, body: UpdateGarageRequest) =>
  request<GarageDto>(`/garages/${garageId}`, { method: 'PATCH', body });

export const deleteGarage = (garageId: string) =>
  request<void>(`/garages/${garageId}`, { method: 'DELETE' });

/**
 * Toggle link sharing. Turning it on mints a *fresh* token, so the returned
 * `publicUrl` is the only correct link — never rebuild one from a cached token
 * (§11: revocation has to actually revoke).
 */
export const setGaragePublic = (garageId: string, isPublic: boolean) =>
  request<SetPublicResponse>(`/garages/${garageId}/public`, { method: 'POST', body: { isPublic } });

// --- members --------------------------------------------------------------

export const listMembers = (garageId: string) => request<MemberDto[]>(`/garages/${garageId}/members`);

export const inviteMember = (garageId: string, body: InviteMemberRequest) =>
  request<MemberDto>(`/garages/${garageId}/members`, { method: 'POST', body });

export const removeMember = (garageId: string, userId: string) =>
  request<void>(`/garages/${garageId}/members/${userId}`, { method: 'DELETE' });

// --- cars -----------------------------------------------------------------

export const listCars = (garageId: string) => request<CarDto[]>(`/garages/${garageId}/cars`);

export const createCar = (garageId: string, body: CreateCarRequest) =>
  request<CarDto>(`/garages/${garageId}/cars`, { method: 'POST', body });

export const updateCar = (garageId: string, carId: string, body: UpdateCarRequest) =>
  request<CarDto>(`/garages/${garageId}/cars/${carId}`, { method: 'PATCH', body });

export const deleteCar = (garageId: string, carId: string) =>
  request<void>(`/garages/${garageId}/cars/${carId}`, { method: 'DELETE' });

/**
 * Ask for a presigned POST. The client must already have resized below
 * `IMAGE_MAX_BYTES` — the API refuses to mint a policy for a larger declared
 * length, and S3 enforces the real cap regardless of what we declare (§7).
 */
export const createImageUpload = (garageId: string, carId: string, body: CreateImageUploadRequest) =>
  request<CreateImageUploadResponse>(`/garages/${garageId}/cars/${carId}/image`, { method: 'POST', body });

// --- drivers --------------------------------------------------------------

export const listDrivers = (garageId: string) => request<DriverDto[]>(`/garages/${garageId}/drivers`);

export const createDriver = (garageId: string, body: CreateDriverRequest) =>
  request<DriverDto>(`/garages/${garageId}/drivers`, { method: 'POST', body });

export const updateDriver = (garageId: string, driverId: string, body: UpdateDriverRequest) =>
  request<DriverDto>(`/garages/${garageId}/drivers/${driverId}`, { method: 'PATCH', body });

export const deleteDriver = (garageId: string, driverId: string) =>
  request<void>(`/garages/${garageId}/drivers/${driverId}`, { method: 'DELETE' });

// --- items ----------------------------------------------------------------

export const listItems = (garageId: string) => request<ItemDto[]>(`/garages/${garageId}/items`);

export const createItem = (garageId: string, body: CreateItemRequest) =>
  request<ItemDto>(`/garages/${garageId}/items`, { method: 'POST', body });

/** Create a whole kit at once — one atomic transaction, no partial success. */
export const createItems = (garageId: string, body: CreateItemsRequest) =>
  request<ItemDto[]>(`/garages/${garageId}/items/bulk`, { method: 'POST', body });

export const updateItem = (garageId: string, itemId: string, body: UpdateItemRequest) =>
  request<ItemDto>(`/garages/${garageId}/items/${itemId}`, { method: 'PATCH', body });

export const deleteItem = (garageId: string, itemId: string) =>
  request<void>(`/garages/${garageId}/items/${itemId}`, { method: 'DELETE' });

// --- service logs: owner or crew -----------------------------------------

export const listServiceLogs = (garageId: string, itemId: string) =>
  request<ServiceLogDto[]>(`/garages/${garageId}/items/${itemId}/service-logs`);

export const createServiceLog = (garageId: string, itemId: string, body: CreateServiceLogRequest) =>
  request<ServiceLogDto>(`/garages/${garageId}/items/${itemId}/service-logs`, { method: 'POST', body });

/** Undo. The API rolls `lastServicedAt` back to the log that now precedes it. */
export const deleteServiceLog = (garageId: string, itemId: string, logId: string) =>
  request<void>(`/garages/${garageId}/items/${itemId}/service-logs/${logId}`, { method: 'DELETE' });

export const markChecked = (garageId: string, itemId: string, body: MarkCheckedRequest) =>
  request<ItemDto>(`/garages/${garageId}/items/${itemId}/checked`, { method: 'POST', body });

// --- events ---------------------------------------------------------------

export const listEvents = (garageId: string) => request<EventDto[]>(`/garages/${garageId}/events`);

export const createEvent = (garageId: string, body: CreateEventRequest) =>
  request<EventDto>(`/garages/${garageId}/events`, { method: 'POST', body });

export const updateEvent = (garageId: string, eventId: string, body: UpdateEventRequest) =>
  request<EventDto>(`/garages/${garageId}/events/${eventId}`, { method: 'PATCH', body });

export const deleteEvent = (garageId: string, eventId: string) =>
  request<void>(`/garages/${garageId}/events/${eventId}`, { method: 'DELETE' });

/**
 * "This is what we actually ran." Converts a planned event to `actual` and
 * records the real passes/miles, which advances usage for every counter on the
 * car (routes/events.ts). The API 409s on a second call, so the UI only ever
 * offers this on a still-planned event.
 */
export const completeEvent = (garageId: string, eventId: string, body: CompleteEventRequest) =>
  request<EventDto>(`/garages/${garageId}/events/${eventId}/actual`, { method: 'POST', body });

// --- views: the server runs the projection engine -------------------------

export const getTimeline = (garageId: string, carId?: string) =>
  request<TimelineResponse>(`/garages/${garageId}/timeline`, { query: { carId } });

export const getDashboard = (garageId: string) => request<DashboardResponse>(`/garages/${garageId}/dashboard`);

/** The all-time record — past events and logged services. The timeline's twin. */
export const getHistory = (garageId: string) => request<HistoryResponse>(`/garages/${garageId}/history`);

// --- public: no auth, no writes ------------------------------------------

/**
 * The only anonymous call in the app. `anonymous: true` is load-bearing: the
 * public view must render for a signed-out visitor, so the client must not
 * demand a token before it will even try (§11).
 */
export const getPublicGarage = (token: string) =>
  request<PublicGarageResponse>(`/public/${token}`, { anonymous: true });

// --- admin (§9) -----------------------------------------------------------

export const listUsers = (query: ListUsersQuery) =>
  request<ListUsersResponse>('/admin/users', { query: { q: query.q, limit: query.limit, offset: query.offset } });

export const getUserDetail = (userId: string) => request<AdminUserDetail>(`/admin/users/${userId}`);

export const setUserDisabled = (userId: string, disabled: boolean) =>
  request<{ id: string; disabledAt: string | null }>(`/admin/users/${userId}/disabled`, {
    method: 'POST',
    body: { disabled },
  });

export const deleteUser = (userId: string, body: DeleteUserRequest) =>
  request<DeleteUserResponse>(`/admin/users/${userId}`, { method: 'DELETE', body });

export const getStats = (days: number) => request<StatsResponse>('/admin/stats', { query: { days } });

/**
 * Never throws for "costs are off" — it answers `{ available: false }` and the
 * UI hides the card entirely (§9). A cost widget must not be able to break the
 * admin page.
 */
export const getCosts = (days: number) => request<CostsResponse>('/admin/costs', { query: { days } });
